import { inject, Injectable, signal } from '@angular/core';
import { SasTokenService } from '../sas-token/sas-token.service';
import { catchError, EMPTY, filter, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { LocalStorageKeys } from '@app/utils/enums/local-storage';
import { UserDataResponse } from '@app/utils/interfaces/user-data';
import { ApiEndpoints } from '@app/utils/enums/api-endpoints';
import { environment } from '@env/environment';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { UpdatePhotoService } from '@app/pages/home-page/user-profile/services/update-photo.service';

@Injectable({
  providedIn: 'root',
})
export class UserStateService {
  public userData = signal<UserDataResponse | null>(null);
  public photoUrl = signal<string>('');
  public headerPhotoUrl = signal<string>('');
  private readonly sasTokenService = inject(SasTokenService);
  private readonly httpClient = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly updatePhotoService = inject(UpdatePhotoService);
  private sasToken: string | null = null;

  public updateUserState(): Observable<string | null> {
    return this.getCurrentUserData().pipe(
      filter((response: UserDataResponse | null) => response !== null),
      tap((response: UserDataResponse) => {
        this.userData.set(response);
        localStorage.setItem(LocalStorageKeys.STAFF_ID, response.data.id);
      }),
      switchMap((response: UserDataResponse) => {
        const imageUrl: string | null = response.data.profileImageUrl;

        if (!imageUrl) {
          return of(null);
        }

        if (!this.sasToken || SasTokenService.isSasTokenExpired(this.sasToken)) {
          return this.sasTokenService.getSasToken(imageUrl).pipe(
            map((token: string) => {
              this.sasToken = token;

              return `${imageUrl}?${this.sasToken}`;
            }),
          );
        }

        return of(`${imageUrl}?${this.sasToken}`);
      }),
      catchError(() => {
        return of(null);
      }),
      tap((fullUrl: string | null) => {
        const url = fullUrl ?? '';
        this.photoUrl.set(url);
        this.headerPhotoUrl.set(url);
      }),
    );
  }

  public deletePhoto(): Observable<null> {
    const id = localStorage.getItem(LocalStorageKeys.STAFF_ID);

    if (!id) return EMPTY;

    return this.updatePhotoService.deletePhoto(id).pipe(
      take(1),
      tap(() => {
        this.setPhotoUrl('');
      }),
      catchError(() => {
        this.dialog.open(StatusModalComponent, {
          data: {
            isSuccess: false,
            title: 'user-profile.general.errors.photoTitle',
            description: 'user-profile.general.errors.photoText',
          },
        });

        return EMPTY;
      }),
    );
  }

  public setPhotoUrl(url: string): void {
    this.photoUrl.set(url);
    this.sasToken = null;
  }

  public resetState(): void {
    this.photoUrl.set('');
    this.headerPhotoUrl.set('');
    this.sasToken = null;
    this.userData.set(null);
  }

  public applyAvatarChanges(): void {
    this.headerPhotoUrl.set(this.photoUrl());
  }

  public resetAvatarChanges(): void {
    this.photoUrl.set(this.headerPhotoUrl());
  }

  public getCurrentUserData(): Observable<UserDataResponse | null> {
    return this.httpClient
      .get<UserDataResponse>(`${environment.apiBaseUrl}${ApiEndpoints.GET_PERSONAL_INFORMATION}`)
      .pipe(
        take(1),
        catchError(() => {
          this.userData.set(null);
          return of(null);
        }),
      );
  }
}
